import { Children, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CDashboardModulePageAction, Context, PageAction, PageActions, PageActionsPayload } from "./types";
import DashboardPageContext from "./DashboardPageContext";
import DashboardEnvContext from "./DashboardEnvContext";
import PagetActionsContext from "./PagetActionsContext";

interface ComponentProps {
	actions: CDashboardModulePageAction[];
	children: ReactNode;
}

function Component({ actions, children }: ComponentProps) {
	const dashboard = useContext(DashboardEnvContext);
	const pageContext = useContext(DashboardPageContext);

	const [actionsToRun, setActionsToRun] = useState<{ chain: PageAction[]; input: PageActionsPayload; onComplete?: () => void }>();

	const buildPageAction = (action: CDashboardModulePageAction) => {
		const scripts = [] as PageAction[];
		if (action.beforeAction) scripts.push(eval(`async ({ dashboard, page, context, actions, params, updateContext, actionPayload, row }) => {context = JSON.parse(JSON.stringify(context)); try {\n${action.beforeAction}\n} finally {updateContext(JSON.parse(JSON.stringify(context)));}}`));
		scripts.push(eval(`async ({ dashboard, page, context, actions, params, updateContext, actionPayload, row }) => {context = JSON.parse(JSON.stringify(context)); try {\n${action.payload}\n} finally {updateContext(JSON.parse(JSON.stringify(context)));}}`));
		if (action.afterAction) scripts.push(eval(`async ({ dashboard, page, context, actions, params, updateContext, actionPayload, row }) => {context = JSON.parse(JSON.stringify(context)); try {\n${action.afterAction}\n} finally {updateContext(JSON.parse(JSON.stringify(context)));}}`));
		return scripts;
	};

	const actionsMap = useMemo(() => {
		const actionsMap = {} as { [key: string]: CDashboardModulePageAction };
		if (actions) {
			actions.forEach((action: CDashboardModulePageAction) => {
				actionsMap[action.name] = action;
			});
		}
		return actionsMap;
	}, [actions]);

	const compiledPageActions = useMemo(() => {
		const actionsMap = {} as { [key: string]: PageAction[] };
		if (actions) {
			actions.forEach((action: CDashboardModulePageAction) => {
				actionsMap[action.name] = buildPageAction(action);
			});
		}
		return actionsMap;
	}, [actions]);

	useEffect(() => {
		if (actionsToRun && pageContext.context) {
			async function runNextAction() {
				if (actionsToRun && dashboard && pageContext.context && compiledPageActions) {
					if (actionsToRun.chain.length != 0) {
						try {
							await actionsToRun.chain[0]({
								dashboard: dashboard,
								page: pageContext,
								context: pageContext.context,
								actions: compiledPageActions,
								params: pageContext.params,
								updateContext: pageContext.updateContext,
								actionPayload: actionsToRun.input.actionPayload,
								row: actionsToRun.input.row,
							});
							actionsToRun.chain.splice(0, 1);
							setActionsToRun({ ...actionsToRun });
						} catch (error) {
							(dashboard.utils as any)["showNotification"]({ message: "Action failed: " + (error as any).message, severity: "error" });
							setActionsToRun(undefined);
						}
						return;
					}
					if (actionsToRun.chain.length == 0 && actionsToRun.onComplete) {
						actionsToRun.onComplete();
					}
					setActionsToRun(undefined);
				}
			}
			runNextAction();
		}
	}, [actionsToRun]);

	const executePageAction = useCallback((actionName: string, input: PageActionsPayload, onComplete?: () => void) => {
		if (compiledPageActions) {
			if (actionsMap[actionName].activatePageLoader) pageContext.activatePageLoader(true);
			setActionsToRun({
				chain: [...compiledPageActions[actionName]],
				input,
				onComplete: () => {
					if (onComplete) onComplete();
					if (actionsMap[actionName].activatePageLoader) pageContext.activatePageLoader(false);
				},
			});
		}
	}, []);

	return (
		<PagetActionsContext.Provider value={{ executePageAction: executePageAction }}>
			<>{children}</>
		</PagetActionsContext.Provider>
	);
}

export default Component;
